import { jsPDF } from "jspdf";

// Palette
const KW_RED = [180, 1, 1];
const KW_CHARCOAL = [43, 42, 41];
const KW_TEAL = [47, 180, 160];
const KW_GRAY = [122, 119, 112];
const KW_LIGHT = [231, 227, 219];
const KW_CREAM = [247, 245, 241];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const statusLabel = (s) =>
  s === "verified" ? "Verified" : s === "needs_attention" ? "Needs Attention" : "Pending";

const wrap = (doc, text, maxWidth) => doc.splitTextToSize(text || "—", maxWidth);

export const downloadReportPDF = async (report) => {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 48;
  const contentWidth = pageWidth - marginX * 2;
  let y = 0;

  // --- Header ---
  doc.setFillColor(...KW_RED);
  doc.rect(0, 0, pageWidth, 6, "F");
  y = 44;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...KW_RED);
  doc.text("KW · AGENT SUCCESS COACHING", marginX, y);
  y += 22;

  doc.setFontSize(22);
  doc.setTextColor(...KW_CHARCOAL);
  doc.text("Open House Activity Report", marginX, y);
  y += 20;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...KW_GRAY);
  doc.text(`Submitted ${formatDate(report.created_at)}`, marginX, y);
  y += 8;

  // Status pill
  const status = report.status || "pending";
  const pillColor =
    status === "verified" ? KW_TEAL : status === "needs_attention" ? KW_RED : KW_GRAY;
  const pillText = statusLabel(status).toUpperCase();
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  const pillW = doc.getTextWidth(pillText) + 18;
  doc.setFillColor(...pillColor);
  doc.roundedRect(pageWidth - marginX - pillW, y - 4, pillW, 18, 9, 9, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(pillText, pageWidth - marginX - pillW + 9, y + 8);

  y += 26;
  doc.setDrawColor(...KW_LIGHT);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 22;

  // --- Section renderer ---
  const rows = [
    ["Agent Name", report.name],
    ["Email", report.email],
    ["Phone", report.phone],
    ["Open House Address", report.address],
    ["Listing Agent", report.listing_agent || "Not provided"],
    [
      "Contacted Listing Agent Prior?",
      (report.contacted_listing_agent_prior || "").toUpperCase() || "—",
    ],
    [
      "Enrolled in Agent Success Program?",
      report.enrolled_in_program ? report.enrolled_in_program.toUpperCase() : "Not answered",
    ],
    ["Report ID", report.id || "—"],
  ];

  const labelWidth = 190;
  const valueX = marginX + labelWidth;
  const valueWidth = contentWidth - labelWidth;

  const ensureRoom = (needed) => {
    if (y + needed > pageHeight - 60) {
      addFooter();
      doc.addPage();
      y = 60;
    }
  };

  doc.setFontSize(10);
  rows.forEach(([label, value], i) => {
    const lines = wrap(doc, String(value ?? "—"), valueWidth);
    const rowHeight = Math.max(24, lines.length * 14 + 10);
    ensureRoom(rowHeight);

    if (i % 2 === 0) {
      doc.setFillColor(...KW_CREAM);
      doc.rect(marginX, y - 4, contentWidth, rowHeight, "F");
    }

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...KW_GRAY);
    doc.setFontSize(8);
    doc.text(label.toUpperCase(), marginX + 8, y + 10);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...KW_CHARCOAL);
    doc.setFontSize(11);
    doc.text(lines, valueX, y + 10);

    y += rowHeight;
  });

  // --- Attached Images ---
  const images = [];
  if (report.sign_in_sheet?.base64) {
    images.push({ label: "Sign In Sheet", ...report.sign_in_sheet });
  }
  if (report.social_media?.base64) {
    images.push({ label: "Social Media Screenshot", ...report.social_media });
  }

  if (images.length > 0) {
    y += 24;
    ensureRoom(24);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...KW_RED);
    doc.text("ATTACHED PHOTOS", marginX, y);
    y += 16;
    doc.setDrawColor(...KW_LIGHT);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 20;

    for (const img of images) {
      const targetW = contentWidth;
      // Compute image display height via canvas-based measurement (we know images are JPEG)
      const dims = await new Promise((resolve) => {
        const el = new Image();
        el.onload = () => resolve({ w: el.naturalWidth, h: el.naturalHeight });
        el.onerror = () => resolve({ w: 800, h: 600 });
        el.src = `data:image/jpeg;base64,${img.base64}`;
      });
      const scale = targetW / dims.w;
      const drawH = Math.min(dims.h * scale, 420);
      const drawW = drawH === 420 ? (dims.w * 420) / dims.h : targetW;

      ensureRoom(drawH + 32);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...KW_CHARCOAL);
      doc.text(img.label, marginX, y);
      y += 8;

      try {
        doc.addImage(
          `data:image/jpeg;base64,${img.base64}`,
          "JPEG",
          marginX,
          y,
          drawW,
          drawH
        );
      } catch (e) {
        doc.setTextColor(...KW_RED);
        doc.text("(Could not embed image)", marginX, y + 12);
      }
      y += drawH + 20;
    }
  }

  addFooter();

  function addFooter() {
    const footY = pageHeight - 36;
    doc.setDrawColor(...KW_LIGHT);
    doc.line(marginX, footY - 12, pageWidth - marginX, footY - 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...KW_GRAY);
    doc.text("KW Wellington · Agent Success Coaching", marginX, footY);
    doc.text(
      `Generated ${new Date().toLocaleString("en-US")}`,
      pageWidth - marginX,
      footY,
      { align: "right" }
    );
  }

  const safeName = (report.name || "report").replace(/[^a-z0-9]+/gi, "_");
  doc.save(`open-house-report_${safeName}_${(report.id || "").slice(0, 8)}.pdf`);
};
